import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";
import { X, User, Phone, Mail, Info, Plus, Trash2, Loader2 } from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const countryCodes = [
  { code: "1", country: "Dominicana/USA/PR", flag: "🇩🇴" },
  { code: "52", country: "México", flag: "🇲🇽" },
  { code: "57", country: "Colombia", flag: "🇨🇴" },
  { code: "34", country: "España", flag: "🇪🇸" },
  { code: "55", country: "Brasil", flag: "🇧🇷" },
  { code: "54", country: "Argentina", flag: "🇦🇷" },
  { code: "56", country: "Chile", flag: "🇨🇱" },
  { code: "51", country: "Perú", flag: "🇵🇪" },
  { code: "593", country: "Ecuador", flag: "🇪🇨" },
  { code: "58", country: "Venezuela", flag: "🇻🇪" },
  { code: "506", country: "Costa Rica", flag: "🇨🇷" },
  { code: "502", country: "Guatemala", flag: "🇬🇹" },
  { code: "503", country: "El Salvador", flag: "🇸🇻" },
  { code: "504", country: "Honduras", flag: "🇭🇳" },
  { code: "505", country: "Nicaragua", flag: "🇳🇮" },
  { code: "507", country: "Panamá", flag: "🇵🇦" },
  { code: "591", country: "Bolivia", flag: "🇧🇴" },
  { code: "595", country: "Paraguay", flag: "🇵🇾" },
  { code: "598", country: "Uruguay", flag: "🇺🇾" },
  { code: "39", country: "Italia", flag: "🇮🇹" },
  { code: "351", country: "Portugal", flag: "🇵🇹" },
  { code: "", country: "Otro (Sin prefijo)", flag: "🌐" }
];

const ContactSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muy corto!").max(50, "Muy largo!").required("Requerido"),
  number: Yup.string().min(6, "Muy corto!").max(50, "Muy largo!"),
  email: Yup.string().email("Email inválido"),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
  const isMounted = useRef(true);
  const initialState = { name: "", number: "", email: "" };
  const [contact, setContact] = useState(initialState);
  const [countryCode, setCountryCode] = useState("1");

  const detectCountryCode = (number) => {
    if (!number) return number;
    const cleanNumber = number.replace(/\D/g, "");
    
    const sortedCodes = [...countryCodes]
      .filter(c => c.code !== "")
      .sort((a, b) => b.code.length - a.code.length);
      
    for (const c of sortedCodes) {
      if (cleanNumber.startsWith(c.code)) {
        setCountryCode(c.code);
        return cleanNumber.substring(c.code.length);
      }
    }
    setCountryCode("");
    return cleanNumber;
  };

  useEffect(() => () => { isMounted.current = false; }, []);

  useEffect(() => {
    const fetchContact = async () => {
      if (initialValues) {
        const cleanNum = detectCountryCode(initialValues.number);
        setContact((prev) => ({ ...prev, ...initialValues, number: cleanNum }));
      }
      if (!contactId) return;
      try {
        const { data } = await api.get(`/contacts/${contactId}`);
        if (isMounted.current) {
          const cleanNum = detectCountryCode(data.number);
          setContact({ ...data, number: cleanNum });
        }
      } catch (err) { toastError(err); }
    };
    fetchContact();
  }, [contactId, open, initialValues]);

  const handleClose = () => { 
    onClose(); 
    setContact(initialState); 
    setCountryCode("1");
  };

  const handleSaveContact = async (values) => {
    try {
      let cleanNumber = values.number ? values.number.replace(/\D/g, "") : "";
      
      if (cleanNumber && countryCode) {
        if (!cleanNumber.startsWith(countryCode)) {
          cleanNumber = `${countryCode}${cleanNumber}`;
        }
      }
      
      const payload = {
        ...values,
        number: cleanNumber
      };

      if (contactId) { 
        await api.put(`/contacts/${contactId}`, payload); 
        handleClose(); 
      }
      else { 
        const { data } = await api.post("/contacts", payload); 
        if (onSave) onSave(data); 
        handleClose(); 
      }
      toast.success(i18n.t("contactModal.success"));
    } catch (err) { toastError(err); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex-none">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <User className="w-4 h-4 text-indigo-600" />
            {contactId ? i18n.t("contactModal.title.edit") : i18n.t("contactModal.title.add")}
          </h2>
          <button onClick={handleClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <Formik
          initialValues={contact}
          enableReinitialize={true}
          validationSchema={ContactSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => { handleSaveContact(values); actions.setSubmitting(false); }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting }) => (
            <Form className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5 bg-slate-50 dark:bg-slate-800/30">
                {/* Main info */}
                <div>
                  <p className="flex items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                    <Info className="w-3.5 h-3.5" /> {i18n.t("contactModal.form.mainInfo")}
                  </p>
                  <div className="space-y-3">
                    {/* Campo Nombre */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                        {i18n.t("contactModal.form.name")}
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Field
                          name="name"
                          type="text"
                          autoFocus
                          placeholder=""
                          className={`w-full pl-9 pr-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                            touched.name && errors.name ? "border-red-400" : "border-slate-200 dark:border-slate-700"
                          }`}
                        />
                      </div>
                      {touched.name && errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                    </div>

                    {/* Campo Número con Selector de País */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                        {i18n.t("contactModal.form.number")}
                      </label>
                      <div className="flex gap-2">
                        <select
                          value={countryCode}
                          onChange={(e) => setCountryCode(e.target.value)}
                          className="px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all cursor-pointer outline-none max-w-[120px]"
                        >
                          {countryCodes.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.flag} {c.code ? `+${c.code}` : "Otro"}
                            </option>
                          ))}
                        </select>
                        <div className="relative flex-1">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Field
                            name="number"
                            type="tel"
                            placeholder="Ej. 8091234567"
                            className={`w-full pl-9 pr-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                              touched.number && errors.number ? "border-red-400" : "border-slate-200 dark:border-slate-700"
                            }`}
                          />
                        </div>
                      </div>
                      {touched.number && errors.number && <p className="text-xs text-red-500 mt-1">{errors.number}</p>}
                    </div>

                    {/* Campo Email */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                        {i18n.t("contactModal.form.email")}
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Field
                          name="email"
                          type="email"
                          placeholder="email@ejemplo.com"
                          className={`w-full pl-9 pr-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                            touched.email && errors.email ? "border-red-400" : "border-slate-200 dark:border-slate-700"
                          }`}
                        />
                      </div>
                      {touched.email && errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                    </div>
                  </div>
                </div>

                {/* Extra info */}
                <div>
                  <p className="flex items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                    <Info className="w-3.5 h-3.5" /> {i18n.t("contactModal.form.extraInfo")}
                  </p>
                  <FieldArray name="extraInfo">
                    {({ push, remove }) => (
                      <div className="space-y-2">
                        {values.extraInfo?.map((info, index) => (
                          <div key={index} className="flex items-center gap-2 p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                            <Field
                              name={`extraInfo[${index}].name`}
                              placeholder={i18n.t("contactModal.form.extraName")}
                              className="flex-1 px-2.5 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                            <Field
                              name={`extraInfo[${index}].value`}
                              placeholder={i18n.t("contactModal.form.extraValue")}
                              className="flex-1 px-2.5 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                            <button type="button" onClick={() => remove(index)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors flex-shrink-0">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => push({ name: "", value: "" })}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold border-2 border-dashed border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 rounded-lg hover:border-indigo-400 hover:text-indigo-600 dark:hover:border-indigo-600 dark:hover:text-indigo-400 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          {i18n.t("contactModal.buttons.addExtraInfo")}
                        </button>
                      </div>
                    )}
                  </FieldArray>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex-none">
                <button type="button" onClick={handleClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                  {i18n.t("contactModal.buttons.cancel")}
                </button>
                <button type="submit" disabled={isSubmitting} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                  {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {contactId ? i18n.t("contactModal.buttons.okEdit") : i18n.t("contactModal.buttons.okAdd")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ContactModal;
